import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import {
  LayoutDashboard, Zap, Wallet, User, LifeBuoy,
  Package, Users, Settings, ScrollText, Network, AlertTriangle, Receipt, Store, CreditCard, Megaphone, Ticket, TrendingUp, Bot,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";

import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import DashboardLayout from "@/components/DashboardLayout";
import FloatingSupport from "@/components/FloatingSupport";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import TicketDetail from "@/pages/TicketDetail";
import VincularTelegram from "@/pages/VincularTelegram";
import StorePage from "@/pages/Store";
import ResellerStore from "@/pages/reseller/ResellerStore";
import ResellerSales from "@/pages/reseller/ResellerSales";
import ResellerGateways from "@/pages/reseller/ResellerGateways";
import ResellerPackages from "@/pages/reseller/ResellerPackages";

import UserOverview from "@/pages/user/UserOverview";
import UserActivations from "@/pages/user/UserActivations";
import UserCredits from "@/pages/user/UserCredits";
import UserAccount from "@/pages/user/UserAccount";
import UserTickets from "@/pages/user/UserTickets";

import AdminOverview from "@/pages/admin/AdminOverview";
import AdminApps from "@/pages/admin/AdminApps";
import AdminPackages from "@/pages/admin/AdminPackages";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminUserProfile from "@/pages/admin/AdminUserProfile";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminMcp from "@/pages/admin/AdminMcp";
import AdminGateways from "@/pages/admin/AdminGateways";
import AdminBroadcast from "@/pages/admin/AdminBroadcast";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminFinance from "@/pages/admin/AdminFinance";
import AdminAudit from "@/pages/admin/AdminAudit";

import ResellerOverview from "@/pages/reseller/ResellerOverview";
import ResellerNetwork from "@/pages/reseller/ResellerNetwork";

import SupportTickets from "@/pages/support/SupportTickets";
import AdminFailures from "@/pages/admin/AdminFailures";
import AdminOrders from "@/pages/admin/AdminOrders";
import NotFound from "@/pages/NotFound";

const userNav = [
  { to: "/painel", label: "Início", icon: LayoutDashboard },
  { to: "/painel/ativacoes", label: "Ativações", icon: Zap },
  { to: "/painel/creditos", label: "Meus créditos", icon: Wallet },
  { to: "/painel/suporte", label: "Suporte", icon: LifeBuoy },
  { to: "/painel/conta", label: "Minha conta", icon: User },
];

const adminNav = [
  { to: "/admin", label: "Visão geral", icon: LayoutDashboard },
  { to: "/admin/aplicativos", label: "Aplicativos", icon: Package },
  { to: "/admin/pacotes", label: "Pacotes", icon: Wallet },
  { to: "/admin/pagamentos", label: "Pagamentos", icon: Receipt },
  { to: "/admin/usuarios", label: "Usuários", icon: Users },
  { to: "/admin/financeiro", label: "Financeiro", icon: TrendingUp },
  { to: "/admin/falhas", label: "Falhas", icon: AlertTriangle },
  { to: "/admin/suporte", label: "Suporte", icon: LifeBuoy },
  { to: "/admin/rede", label: "Revendedores", icon: Network },
  { to: "/admin/gateways", label: "Gateways", icon: CreditCard },
  { to: "/admin/broadcast", label: "Disparos", icon: Megaphone },
  { to: "/admin/cupons", label: "Cupons", icon: Ticket },
  { to: "/admin/logs", label: "Logs & Auditoria", icon: ScrollText },
  { to: "/admin/mcp", label: "Claude (MCP)", icon: Bot },
  { to: "/admin/config", label: "Configurações", icon: Settings },
];

const resellerNav = [
  { to: "/revenda", label: "Visão geral", icon: LayoutDashboard },
  { to: "/revenda/loja", label: "Minha loja", icon: Store },
  { to: "/revenda/recebimentos", label: "Recebimentos", icon: CreditCard },
  { to: "/revenda/pacotes", label: "Pacotes", icon: Wallet },
  { to: "/revenda/vendas", label: "Vendas", icon: Receipt },
  { to: "/revenda/rede", label: "Minha rede", icon: Network },
  { to: "/revenda/ativacoes", label: "Ativações", icon: Zap },
  { to: "/revenda/creditos", label: "Créditos", icon: Wallet },
  { to: "/revenda/suporte", label: "Suporte", icon: LifeBuoy },
];

const supportNav = [
  { to: "/suporte", label: "Tickets", icon: LifeBuoy },
  { to: "/suporte/falhas", label: "Falhas", icon: AlertTriangle },
];

function BodyOverlayGuard() {
  // Bulletproof fix for the Radix leftover bug: overlays (Dialog/Select/Popover/
  // DropdownMenu) set `pointer-events:none` on <body>. If they unmount without
  // restoring it, the whole app (sidebar included) becomes unclickable. This guard
  // clears the lock whenever it is stuck with no overlay actually open.
  useEffect(() => {
    const clearIfStuck = () => {
      if (document.body.style.pointerEvents !== "none") return;
      const open = document.querySelector(
        '[data-radix-popper-content-wrapper],[role="dialog"][data-state="open"],[role="alertdialog"][data-state="open"],[role="menu"][data-state="open"]'
      );
      if (!open) {
        document.body.style.pointerEvents = "";
        document.body.style.removeProperty("overflow");
        document.body.removeAttribute("data-scroll-locked");
      }
    };
    const obs = new MutationObserver(clearIfStuck);
    obs.observe(document.body, { attributes: true, attributeFilter: ["style"] });
    const iv = setInterval(clearIfStuck, 400);
    return () => { obs.disconnect(); clearInterval(iv); };
  }, []);
  return null;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <BodyOverlayGuard />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/cadastro" element={<Register />} />
            <Route path="/recuperar-senha" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/vincular-telegram" element={<VincularTelegram />} />
            <Route path="/loja/:slug" element={<StorePage />} />

            {/* User dashboard */}
            <Route element={<ProtectedRoute><DashboardLayout nav={userNav} title="Meu painel" /></ProtectedRoute>}>
              <Route path="/painel" element={<UserOverview />} />
              <Route path="/painel/ativacoes" element={<UserActivations />} />
              <Route path="/painel/creditos" element={<UserCredits />} />
              <Route path="/painel/conta" element={<UserAccount />} />
              <Route path="/painel/suporte" element={<UserTickets />} />
              <Route path="/painel/tickets/:id" element={<TicketDetail />} />
            </Route>

            {/* Reseller */}
            <Route element={<ProtectedRoute roles={["reseller", "admin"]}><DashboardLayout nav={resellerNav} title="Revenda" /></ProtectedRoute>}>
              <Route path="/revenda" element={<ResellerOverview />} />
              <Route path="/revenda/loja" element={<ResellerStore />} />
              <Route path="/revenda/recebimentos" element={<ResellerGateways />} />
              <Route path="/revenda/vendas" element={<ResellerSales />} />
              <Route path="/revenda/rede" element={<ResellerNetwork />} />
              <Route path="/revenda/pacotes" element={<ResellerPackages />} />
              <Route path="/revenda/ativacoes" element={<UserActivations />} />
              <Route path="/revenda/creditos" element={<UserCredits />} />
              <Route path="/revenda/suporte" element={<UserTickets />} />
              <Route path="/revenda/tickets/:id" element={<TicketDetail />} />
            </Route>

            {/* Support staff */}
            <Route element={<ProtectedRoute roles={["support", "admin"]}><DashboardLayout nav={supportNav} title="Atendimento" /></ProtectedRoute>}>
              <Route path="/suporte" element={<SupportTickets />} />
              <Route path="/suporte/falhas" element={<AdminFailures />} />
              <Route path="/suporte/tickets/:id" element={<TicketDetail />} />
            </Route>

            {/* Admin */}
            <Route element={<ProtectedRoute roles={["admin"]}><DashboardLayout nav={adminNav} title="Administração" /></ProtectedRoute>}>
              <Route path="/admin" element={<AdminOverview />} />
              <Route path="/admin/aplicativos" element={<AdminApps />} />
              <Route path="/admin/pacotes" element={<AdminPackages />} />
              <Route path="/admin/pagamentos" element={<AdminOrders />} />
              <Route path="/admin/usuarios" element={<AdminUsers />} />
              <Route path="/admin/usuarios/:id" element={<AdminUserProfile />} />
              <Route path="/admin/financeiro" element={<AdminFinance />} />
              <Route path="/admin/falhas" element={<AdminFailures />} />
              <Route path="/admin/suporte" element={<SupportTickets />} />
              <Route path="/admin/rede" element={<ResellerNetwork />} />
              <Route path="/admin/logs" element={<AdminAudit />} />
              <Route path="/admin/config" element={<AdminSettings />} />
              <Route path="/admin/mcp" element={<AdminMcp />} />
              <Route path="/admin/gateways" element={<AdminGateways />} />
              <Route path="/admin/broadcast" element={<AdminBroadcast />} />
              <Route path="/admin/cupons" element={<AdminCoupons />} />
              <Route path="/admin/tickets/:id" element={<TicketDetail />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
          <FloatingSupport />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
